import { reactive, Ref, ref, ShallowRef } from 'vue'
import { PagetableInstance } from '../Pagetable'
import OrionError from '../../../error/OrionError'

interface PaginationProps {
  currentPage: number
  pageSize: number
  pageSizes?: number[]
  total?: number
}

type LoadDataFn<Row, Tail> = (self: {
  data: Row[]
  pagination: PaginationProps
  tailData: Tail | undefined
  toggleRowSelection: (row: Row, selected?: boolean) => void
  toggleAllSelection: () => void
  setCurrentRow: (row: Row) => void
}) => Promise<void>

interface UsePagetableOptions<Row, Tail> {
  tableRef?: Readonly<ShallowRef<PagetableInstance | null>>
  onLoadData: LoadDataFn<Row, Tail>
  afterSelectionChange?: (newSelection: Row[]) => void
  afterCurrentRowChange?: (currentRow: Row, oldCurrentRow: Row) => void
  defaultData?: Row[]
  defaultPagination?: PaginationProps
}

const usePagetable = <Row = any, Tail = any>(options: UsePagetableOptions<Row, Tail>) => {
  const { tableRef, onLoadData, afterSelectionChange, afterCurrentRowChange, defaultData, defaultPagination } = options

  const data = ref<Row[]>(defaultData ? defaultData : []) as Ref<Row[]>
  const pagination = ref<PaginationProps>(
    defaultPagination
      ? defaultPagination
      : {
          currentPage: 1,
          pageSize: 10,
        }
  )
  const tailData = ref<Tail>()
  const selection = ref<Row[]>([]) as Ref<Row[]>
  const currentRow = ref<Row>()

  const onSelectionChange = (newSelection: Row[]) => {
    selection.value = newSelection
    afterSelectionChange && afterSelectionChange(newSelection)
  }
  const onCurrentRowChange = (newCurrentRow: Row, oldCurrentRow: Row) => {
    currentRow.value = newCurrentRow
    afterCurrentRowChange && afterCurrentRowChange(newCurrentRow, oldCurrentRow)
  }

  const clearSelection = () => {
    if (!tableRef) {
      throw new OrionError(`调用clearSelection失败,tableRef为空`)
    }
    tableRef.value!.clearSelection()
  }
  const toggleRowSelection = (row: Row, selected?: boolean) => {
    if (!tableRef) {
      throw new OrionError(`调用toggleRowSelection失败,tableRef为空`)
    }
    tableRef.value!.toggleRowSelection(row, selected)
  }
  const toggleAllSelection = () => {
    if (!tableRef) {
      throw new OrionError(`调用toggleAllSelection失败,tableRef为空`)
    }
    tableRef.value!.toggleAllSelection()
  }
  const getSelectionRows = () => {
    if (!tableRef) {
      throw new OrionError(`调用getSelectionRows失败,tableRef为空`)
    }
    return tableRef.value!.getSelectionRows() as Row[]
  }
  const setCurrentRow = (row: Row) => {
    if (!tableRef) {
      throw new OrionError(`调用setCurrentRow失败,tableRef为空`)
    }
    tableRef.value!.setCurrentRow(row)
  }

  const self = reactive({
    data,
    pagination,
    tailData,
    toggleRowSelection,
    toggleAllSelection,
    setCurrentRow,
  })

  const loadData = async () => {
    await onLoadData(self)
  }

  return reactive({
    data,
    pagination,
    tailData,
    selection,
    currentRow,
    onSelectionChange,
    onCurrentRowChange,
    clearSelection,
    toggleRowSelection,
    toggleAllSelection,
    getSelectionRows,
    setCurrentRow,
    loadData,
  })
}

export default usePagetable
